import React, { useState, useEffect } from 'react';
import '../../../assets/scss/components/ui/modal/ResultsModal.scss';

const ResultsModal = ({ results, onClose, isProcessing = false, theme, type = 'vehicle' }) => {
  // Inicialización de estado mejorada para prevenir undefined
  const { saved = [], errors = [], duplicates = [] } = results?.results || {};
  const [activeTab, setActiveTab] = useState('duplicates');
  const [currentPage, setCurrentPage] = useState(1);
  const [replaceDuplicates, setReplaceDuplicates] = useState({});
  const [duplicateStrategy, setDuplicateStrategy] = useState('merge');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  const itemsPerPage = 5;
  
  // Establecer pestaña activa inicial basada en los datos disponibles
  useEffect(() => {
    if (duplicates && duplicates.length > 0) {
      setActiveTab('duplicates');
    } else if (errors && errors.length > 0) {
      setActiveTab('errors');
    } else if (saved && saved.length > 0) {
      setActiveTab('saved');
    }
  }, [duplicates, errors, saved]);
  
  // Inicializar estado de duplicados cuando los datos están disponibles
  useEffect(() => {
    if (duplicates && duplicates.length > 0) {
      const initialState = duplicates.reduce((acc, item) => {
        if (item.existingRecord && item.existingRecord.id) {
          acc[item.existingRecord.id] = false;
        }
        return acc;
      }, {});
      setReplaceDuplicates(initialState);
    }
  }, [duplicates]);
  
  // Funciones de paginación
  const getPaginatedItems = (items = []) => {
    if (!items || !Array.isArray(items)) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  };
  
  const totalPages = Math.ceil(
    (activeTab === 'saved' ? saved.length : 
     activeTab === 'errors' ? errors.length : duplicates.length) / itemsPerPage || 1
  );
  
  // Restablecer página al cambiar pestaña
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);
  
  // Función para alternar selección de duplicado
  const toggleReplaceStatus = (id) => {
    setReplaceDuplicates(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Contar duplicados seleccionados
  const selectedDuplicatesCount = 
    Object.values(replaceDuplicates).filter(Boolean).length;
  
  // Manejar cierre con actualización
  const handleCloseWithUpdate = () => {
    const updates = duplicates
      .filter(item => item.existingRecord && replaceDuplicates[item.existingRecord.id])
      .map(item => {
        return {
          existingId: item.existingRecord.id,
          newData: item.newRecord,
          strategy: duplicateStrategy,
          type: type // Añadir el tipo
        };
      });
    
    onClose(true, updates);
  };
  
  // Seleccionar/deseleccionar todos los duplicados
  const handleSelectAll = (selectAll) => {
    const newState = {};
    duplicates.forEach(item => {
      if (item.existingRecord && item.existingRecord.id) {
        newState[item.existingRecord.id] = selectAll;
      }
    });
    setReplaceDuplicates(newState);
  };

  return (
    <div className={`results-modal-overlay ${theme}`}>
      <div className={`results-modal ${theme}`}>
        <div className="modal-header">
          <h2 className="modal-title">Resultados del Proceso</h2>
          <button className={`close-button ${theme}`} onClick={() => onClose(false)}>
            <span>×</span>
          </button>
        </div>
        
        <div className={`results-summary ${theme}`}>
          <div className={`summary-item ${saved?.length > 0 ? 'success' : ''}`}>
            <span className="count">{saved?.length || 0}</span>
            <span className="label">Guardados exitosamente</span>
          </div>
          
          <div className={`summary-item ${errors?.length > 0 ? 'error' : ''}`}>
            <span className="count">{errors?.length || 0}</span>
            <span className="label">Con errores</span>
          </div>
          
          <div className={`summary-item ${duplicates?.length > 0 ? 'duplicate' : ''}`}>
            <span className="count">{duplicates?.length || 0}</span>
            <span className="label">Duplicados encontrados</span>
            {duplicates && duplicates.length > 0 && selectedDuplicatesCount > 0 && (
              <span className="selected-count">
                ({selectedDuplicatesCount} seleccionados)
              </span>
            )}
          </div>
        </div>
        
        <div className="tabs-container">
          <div className="tabs-header">
            <button 
              className={`tab-button ${activeTab === 'saved' ? 'active' : ''} ${theme}`}
              onClick={() => setActiveTab('saved')}
              disabled={(saved?.length || 0) === 0}
            >
              Guardados ({saved?.length || 0})
            </button>
            <button 
              className={`tab-button ${activeTab === 'errors' ? 'active' : ''} ${theme}`}
              onClick={() => setActiveTab('errors')}
              disabled={(errors?.length || 0) === 0}
            >
              Errores ({errors?.length || 0})
            </button>
            <button 
              className={`tab-button ${activeTab === 'duplicates' ? 'active' : ''} ${theme}`}
              onClick={() => setActiveTab('duplicates')}
              disabled={(duplicates?.length || 0) === 0}
            >
              Duplicados ({duplicates?.length || 0})
            </button>
          </div>
          
          <div className={`tab-content ${theme}`}>
            {activeTab === 'saved' && saved && saved.length > 0 && (
              <div className="tab-section">
                <table className={`results-table ${theme}`}>
                  <thead>
                    <tr>
                      {type === 'operator' ? (
                        <>
                          <th>Documento</th>
                          <th>Nombres</th>
                          <th>Apellidos</th>
                        </>
                      ) : (
                        <>
                          <th>Placa</th>
                          <th>Operador</th>
                          <th>ID</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedItems(saved).map((item, index) => (
                      <tr key={index}>
                        {type === 'operator' ? (
                          <>
                            <td>{item.operator_id_number}</td>
                            <td>{item.operator_first_names}</td>
                            <td>{item.operator_last_names}</td>
                          </>
                        ) : (
                          <>
                            <td>{item.plate}</td>
                            <td>{item.operator}</td>
                            <td>{item.id}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {activeTab === 'errors' && errors && errors.length > 0 && (
              <div className="tab-section">
                <table className={`results-table error-table ${theme}`}>
                  <thead>
                    <tr>
                      {type === 'operator' ? (
                        <>
                          <th>Documento</th>
                          <th>Nombres</th>
                          <th>Error</th>
                        </>
                      ) : (
                        <>
                          <th>Placa</th>
                          <th>Operador</th>
                          <th>Error</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedItems(errors).map((item, index) => (
                      <tr key={index}>
                        {type === 'operator' ? (
                          <>
                            <td>{item.operator_id_number || 'No disponible'}</td>
                            <td>{item.operator_first_names || 'No disponible'}</td>
                            <td className="error-message">{item.error}</td>
                          </>
                        ) : (
                          <>
                            <td>{item.plate || 'No disponible'}</td>
                            <td>{item.operator || 'No disponible'}</td>
                            <td className="error-message">{item.error}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {activeTab === 'duplicates' && duplicates && duplicates.length > 0 && (
              <div className="tab-section duplicates-section">
                <div className={`duplicates-options ${theme}`}>
                  <div className="select-options">
                    <button 
                      className={`action-button select-all ${theme}`}
                      onClick={() => handleSelectAll(true)}
                      disabled={isProcessing}
                    >
                      Seleccionar todos
                    </button>
                    <button 
                      className={`action-button deselect-all ${theme}`}
                      onClick={() => handleSelectAll(false)}
                      disabled={isProcessing}
                    >
                      Deseleccionar todos
                    </button>
                  
                    <button
                      className={`toggle-advanced ${showAdvancedOptions ? 'active' : ''} ${theme}`}
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      disabled={isProcessing}
                    >
                      {showAdvancedOptions ? 'Ocultar opciones avanzadas' : 'Mostrar opciones avanzadas'}
                    </button>
                  </div>
                  
                  {showAdvancedOptions && (
                    <div className={`strategy-selector ${theme}`}>
                      <h4>Estrategia de actualización:</h4>
                      <div className="strategy-options">
                        <label className={`radio-container ${theme}`}>
                          <input
                            type="radio"
                            name="strategy"
                            value="merge"
                            checked={duplicateStrategy === 'merge'}
                            onChange={() => setDuplicateStrategy('merge')}
                            disabled={isProcessing}
                          />
                          <span className="radio-checkmark"></span>
                          <span>Actualizar solo campos no vacíos (conservar datos existentes)</span>
                        </label>
                        <label className={`radio-container ${theme}`}>
                          <input
                            type="radio"
                            name="strategy"
                            value="replace"
                            checked={duplicateStrategy === 'replace'}
                            onChange={() => setDuplicateStrategy('replace')}
                            disabled={isProcessing}
                          />
                          <span className="radio-checkmark"></span>
                          <span>Reemplazar registro completo (sobreescribir todo)</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="duplicates-list">
                  {getPaginatedItems(duplicates).map((item, index) => (
                    <DuplicateComparison
                      key={index}
                      item={item}
                      isSelected={item.existingRecord && replaceDuplicates[item.existingRecord.id]}
                      onToggle={() => item.existingRecord && toggleReplaceStatus(item.existingRecord.id)}
                      disabled={isProcessing}
                      theme={theme}
                      type={type}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {(activeTab === 'saved' && (!saved || saved.length === 0)) || 
             (activeTab === 'errors' && (!errors || errors.length === 0)) || 
             (activeTab === 'duplicates' && (!duplicates || duplicates.length === 0)) ? (
              <div className={`no-data-message ${theme}`}>
                No hay datos para mostrar en esta categoría
              </div>
            ) : null}
          </div>
          
          {totalPages > 1 && (
            <div className={`pagination-controls ${theme}`}>
              <button 
                className={`pagination-button ${theme}`}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || isProcessing}
              >
                Anterior
              </button>
              <span className="page-info">
                Página {currentPage} de {totalPages}
              </span>
              <button 
                className={`pagination-button ${theme}`}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || isProcessing}
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
        
        <div className={`modal-actions ${theme}`}>
          {isProcessing ? (
            <div className={`processing-indicator ${theme}`}>
              <div className="spinner"></div>
              <span>Procesando actualizaciones...</span>
            </div>
          ) : duplicates && duplicates.length > 0 && selectedDuplicatesCount > 0 ? (
            <>
              <button 
                className={`button secondary ${theme}`}
                onClick={() => onClose(false)}
              >
                Cerrar sin actualizar
              </button>
              <button 
                className={`button primary ${theme}`}
                onClick={handleCloseWithUpdate}
              >
                Actualizar {selectedDuplicatesCount} registro{selectedDuplicatesCount !== 1 ? 's' : ''} y cerrar
              </button>
            </>
          ) : (
            <button 
              className={`button primary ${theme}`}
              onClick={() => onClose(false)}
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const DuplicateComparison = ({ item, isSelected, onToggle, disabled, theme, type = 'vehicle' }) => {
  const [showAllFields, setShowAllFields] = useState(false);
  
  // Validación básica
  if (!item || !item.existingRecord || !item.newRecord) {
    return <div>Datos de duplicado inválidos</div>;
  }
  
  // Seleccionar campos adecuados según el tipo
  let basicFields, additionalFields;
  
  if (type === 'operator') {
    // Campos básicos para operadores
    basicFields = [
      { label: 'Nombres', field: 'operator_first_names' },
      { label: 'Apellidos', field: 'operator_last_names' },
      { label: 'Documento', field: 'operator_id_number' },
      { label: 'Licencia', field: 'operator_license' }
    ];
    
    // Campos adicionales para operadores
    additionalFields = [
      { label: 'Fecha Nacimiento', field: 'operator_birth_date', formatter: formatDate },
      { label: 'Categoría Licencia', field: 'operator_license_category' },
      { label: 'Vence Licencia', field: 'operator_license_expiry', formatter: formatDate },
      { label: 'ARL', field: 'operator_arl' },
      { label: 'EPS', field: 'operator_eps' },
      { label: 'Teléfono', field: 'operator_phone' }
    ];
  } else {
    // Campos básicos para vehículos
    basicFields = [
      { label: 'Placa', field: 'plate' },
      { label: 'Propietario', field: 'owner' },
      { label: 'Operador', 
        getValue: (record) => `${record.operator_first_names || ''} ${record.operator_last_names || ''}`.trim() || 'N/A' },
      { label: 'SOAT', field: 'soat_number' },
      { label: 'Vence SOAT', field: 'soat_expiry', formatter: formatDate }
    ];
    
    // Campos adicionales para vehículos
    additionalFields = [
      { label: 'Tipo Vehículo', field: 'vehicle_type' },
      { label: 'Marca', field: 'brand' },
      { label: 'Modelo', field: 'model' },
      { label: 'Color', field: 'color' },
      { label: 'Técnico-Mecánica', field: 'technical_review' },
      { label: 'Vence Técnico-Mecánica', field: 'technical_review_expiry', formatter: formatDate },
      { label: 'Remolque', field: 'trailer_plate' },
      { label: 'Capacidad', field: 'total_capacity' },
      { label: 'Ejes', field: 'num_axes' }
    ];
  }
  
  // Función para obtener valor
  const getFieldValue = (record, fieldInfo) => {
    if (!record) return 'N/A';
    
    if (fieldInfo.getValue) {
      return fieldInfo.getValue(record);
    }
    
    let value = record[fieldInfo.field];
    
    if (fieldInfo.formatter && value) {
      value = fieldInfo.formatter(value);
    }
    
    return value || 'N/A';
  };
  
  // Determinar el título según el tipo
  const titleField = type === 'operator' ? 'operator_id_number' : 'plate';
  const titleLabel = type === 'operator' ? 'ID' : 'Placa';
  
  return (
    <div className={`duplicate-comparison ${isSelected ? 'selected' : ''} ${theme}`}>
      <div className={`comparison-header ${theme}`}>
        <h4>{titleLabel}: {item.newRecord[titleField]}</h4>
        <button 
          className={`replace-button ${isSelected ? 'active' : ''} ${theme}`}
          onClick={onToggle}
          disabled={disabled}
        >
          {isSelected ? 'Seleccionado para actualizar' : 'Seleccionar para actualizar'}
        </button>
      </div>
      
      <div className={`comparison-content ${theme}`}>
        <div className={`comparison-table-container ${theme}`}>
          <table className={`comparison-table ${theme}`}>
            <thead>
              <tr>
                <th>Campo</th>
                <th>Registro Existente</th>
                <th>Nuevo Registro</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* Campos básicos siempre visibles */}
              {basicFields.map((fieldInfo, index) => {
                const existingValue = getFieldValue(item.existingRecord, fieldInfo);
                const newValue = getFieldValue(item.newRecord, fieldInfo);
                const isDifferent = existingValue !== newValue;
                
                return (
                  <tr key={index} className={isDifferent ? 'different-value' : ''}>
                    <td className="field-label">{fieldInfo.label}</td>
                    <td className="existing-value">{existingValue}</td>
                    <td className="new-value">{newValue}</td>
                    {isDifferent && <td className="diff-indicator" title="Los valores son diferentes">⚠️</td>}
                  </tr>
                );
              })}
              
              {/* Campos adicionales expandibles */}
              {showAllFields && additionalFields.map((fieldInfo, index) => {
                const existingValue = getFieldValue(item.existingRecord, fieldInfo);
                const newValue = getFieldValue(item.newRecord, fieldInfo);
                const isDifferent = existingValue !== newValue;
                
                return (
                  <tr key={`additional-${index}`} className={isDifferent ? 'different-value' : ''}>
                    <td className="field-label">{fieldInfo.label}</td>
                    <td className="existing-value">{existingValue}</td>
                    <td className="new-value">{newValue}</td>
                    {isDifferent && <td className="diff-indicator" title="Los valores son diferentes">⚠️</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <button 
          className={`toggle-fields-button ${theme}`}
          onClick={() => setShowAllFields(!showAllFields)}
          disabled={disabled}
        >
          {showAllFields ? 'Mostrar menos campos' : 'Mostrar más campos'}
        </button>
      </div>
    </div>
  );
};

// Función para formatear fechas
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
    return String(dateString);
  } catch (e) {
    return String(dateString);
  }
};

export default ResultsModal;